/**
 * Per-symbol live market stream for the chart.
 *
 * binanceService subscribes to `!ticker@arr`, which carries every symbol on the exchange
 * but only pushes 24-hour rolling statistics once a second. That is fine for a market
 * list, and far too coarse to build candles from: sampling a 24h summary once a second
 * produces bars whose open/high/low are guesses rather than real trade data.
 *
 * This service instead opens a combined stream for the single symbol the chart is showing:
 *
 *   <symbol>@kline_<interval>  Binance's own candle for the current period, pushed on
 *                              every change, with the authoritative open/high/low/close
 *                              and an `x` flag marking the bar as closed.
 *   <symbol>@trade             Every individual trade, giving a true tick-by-tick price.
 *
 * Sockets are reference counted per symbol+interval so remounts and duplicate consumers
 * share one connection.
 */

export interface LiveKline {
  /** Bar open time in seconds (lightweight-charts time format). */
  time: number;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
  /** True once Binance considers this bar final. */
  isClosed: boolean;
}

export interface LiveTrade {
  price: number;
  quantity: number;
  timestamp: number;
}

export interface LiveStreamHandlers {
  onKline?: (kline: LiveKline) => void;
  onTrade?: (trade: LiveTrade) => void;
  onStatusChange?: (connected: boolean) => void;
}

interface StreamEntry {
  socket: WebSocket | null;
  handlers: Set<LiveStreamHandlers>;
  reconnectAttempts: number;
  reconnectTimer: ReturnType<typeof setTimeout> | null;
  closedByUs: boolean;
  connected: boolean;
}

const BINANCE_STREAM_HOST = 'wss://stream.binance.com:9443/stream';
const MAX_RECONNECT_DELAY_MS = 30000;
const BASE_RECONNECT_DELAY_MS = 1000;

class LiveChartStreamService {
  private streams = new Map<string, StreamEntry>();
  /** Latest traded price per symbol, so late subscribers get a value immediately. */
  private lastPrices = new Map<string, number>();

  private keyFor(symbol: string, interval: string): string {
    return `${symbol.toUpperCase()}|${interval}`;
  }

  /**
   * Subscribe to live candles and trades for one symbol/interval.
   * Returns an unsubscribe function; the socket closes once the last consumer leaves.
   */
  subscribe(symbol: string, interval: string, handlers: LiveStreamHandlers): () => void {
    if (!symbol || !interval) return () => {};

    const key = this.keyFor(symbol, interval);
    let entry = this.streams.get(key);

    if (!entry) {
      entry = {
        socket: null,
        handlers: new Set(),
        reconnectAttempts: 0,
        reconnectTimer: null,
        closedByUs: false,
        connected: false,
      };
      this.streams.set(key, entry);
    }

    entry.handlers.add(handlers);

    if (!entry.socket) {
      this.connect(symbol, interval);
    } else if (entry.connected) {
      handlers.onStatusChange?.(true);
    }

    return () => {
      const current = this.streams.get(key);
      if (!current) return;

      current.handlers.delete(handlers);
      if (current.handlers.size === 0) {
        this.teardown(key);
      }
    };
  }

  /** Most recent traded price seen on the stream, if any. */
  getLastPrice(symbol: string): number | undefined {
    return this.lastPrices.get(symbol.toUpperCase());
  }

  private connect(symbol: string, interval: string): void {
    const key = this.keyFor(symbol, interval);
    const entry = this.streams.get(key);
    if (!entry) return;

    // Binance requires lower-case stream names.
    const lower = symbol.toLowerCase();
    const url = `${BINANCE_STREAM_HOST}?streams=${lower}@kline_${interval}/${lower}@trade`;

    let socket: WebSocket;
    try {
      socket = new WebSocket(url);
    } catch (error) {
      console.warn(`liveChartStream: could not open socket for ${key}`, error);
      this.scheduleReconnect(symbol, interval);
      return;
    }

    entry.socket = socket;
    entry.closedByUs = false;

    socket.onopen = () => {
      const active = this.streams.get(key);
      if (!active) return;
      active.reconnectAttempts = 0;
      active.connected = true;
      active.handlers.forEach((h) => h.onStatusChange?.(true));
    };

    socket.onmessage = (event) => {
      try {
        const payload = JSON.parse(event.data);
        this.dispatch(key, symbol, payload);
      } catch (error) {
        console.warn('liveChartStream: malformed message', error);
      }
    };

    socket.onerror = () => {
      // onclose always follows, which is where reconnection is handled.
    };

    socket.onclose = () => {
      const active = this.streams.get(key);
      if (!active) return;

      active.connected = false;
      active.socket = null;
      active.handlers.forEach((h) => h.onStatusChange?.(false));

      if (!active.closedByUs) {
        this.scheduleReconnect(symbol, interval);
      }
    };
  }

  private dispatch(key: string, symbol: string, payload: any): void {
    const entry = this.streams.get(key);
    if (!entry) return;

    const stream: string | undefined = payload?.stream;
    const data = payload?.data;
    if (!stream || !data) return;

    if (stream.includes('@kline')) {
      const k = data.k;
      if (!k) return;

      const kline: LiveKline = {
        time: Math.floor(Number(k.t) / 1000),
        open: parseFloat(k.o),
        high: parseFloat(k.h),
        low: parseFloat(k.l),
        close: parseFloat(k.c),
        volume: parseFloat(k.v),
        isClosed: Boolean(k.x),
      };

      // Guard the chart against partial or malformed frames.
      if (
        !Number.isFinite(kline.time) ||
        !Number.isFinite(kline.open) ||
        !Number.isFinite(kline.high) ||
        !Number.isFinite(kline.low) ||
        !Number.isFinite(kline.close)
      ) {
        return;
      }

      this.lastPrices.set(symbol.toUpperCase(), kline.close);
      entry.handlers.forEach((h) => {
        try {
          h.onKline?.(kline);
        } catch (error) {
          console.warn('liveChartStream: kline handler failed', error);
        }
      });
      return;
    }

    if (stream.includes('@trade')) {
      const price = parseFloat(data.p);
      if (!Number.isFinite(price)) return;

      const trade: LiveTrade = {
        price,
        quantity: parseFloat(data.q) || 0,
        timestamp: Number(data.T) || Date.now(),
      };

      this.lastPrices.set(symbol.toUpperCase(), price);
      entry.handlers.forEach((h) => {
        try {
          h.onTrade?.(trade);
        } catch (error) {
          console.warn('liveChartStream: trade handler failed', error);
        }
      });
    }
  }

  private scheduleReconnect(symbol: string, interval: string): void {
    const key = this.keyFor(symbol, interval);
    const entry = this.streams.get(key);
    if (!entry || entry.handlers.size === 0 || entry.reconnectTimer) return;

    entry.reconnectAttempts += 1;
    const delay = Math.min(
      BASE_RECONNECT_DELAY_MS * 2 ** (entry.reconnectAttempts - 1),
      MAX_RECONNECT_DELAY_MS,
    );

    entry.reconnectTimer = setTimeout(() => {
      const active = this.streams.get(key);
      if (!active) return;

      active.reconnectTimer = null;
      if (active.handlers.size > 0) {
        this.connect(symbol, interval);
      }
    }, delay);
  }

  private teardown(key: string): void {
    const entry = this.streams.get(key);
    if (!entry) return;

    entry.closedByUs = true;

    if (entry.reconnectTimer) {
      clearTimeout(entry.reconnectTimer);
      entry.reconnectTimer = null;
    }

    if (entry.socket) {
      entry.socket.onopen = null;
      entry.socket.onmessage = null;
      entry.socket.onerror = null;
      entry.socket.onclose = null;
      try {
        entry.socket.close();
      } catch (error) {
        // Already closing; nothing to do.
      }
      entry.socket = null;
    }

    this.streams.delete(key);
  }
}

export const liveChartStream = new LiveChartStreamService();
export default liveChartStream;
